import { getEbMenuGetMenusByUser } from "@zkt/open-api/esm/oauth2";
import {
  Cube,
  House,
  BagHalfCircle,
  CreditCard,
  Tag,
  Ticket,
  DollerCircle,
  Person,
  ChartLine,
  WechatEnt,
  Note,
  Trend,
  Gear,
  Cutlery,
  Share,
  Sever,
  Stack,
} from "@zkt/icons";
import { Brand, http, Merchant, Auth, User, config } from "@zkt/eb-utility";
import React from "react";
import { Menu } from "./model";
import { NotifyConfig } from "./notify/interface";

/**
 * 新版图标映射老版图标
 */
const icons = {
  "fa-cube": Cube,
  "fa-building": House,
  "fa-ticket": BagHalfCircle,
  "fa-credit-card": CreditCard,
  "fa-calendar": Tag,
  "fa-cutlery": Cutlery,
  "fa-tree": Ticket,
  "fa-check": DollerCircle,
  "fa-group": Person,
  "fa-wechat": WechatEnt,
  "fa-dollar": Stack,
  "fa-line-chart": ChartLine,
  "fa-server": Sever,
  "fa-handshake-o": Note,
  "fa-comment-o": Trend,
  "fa-hospital-o": Gear,
};

function getLink(item, link: (item) => string = (x) => x.url) {
  let result = "";
  const href = link(item);
  if (item?.type === "custom") {
    return href;
  }
  if (item?.type === "hotel") {
    result = `/iframe/${btoa(href)}/hotel`;
  } else if (!item.type || item?.type === "ebooking" || item?.type === "eb") {
    result = href;
  }
  if (!result || item.type === "folder" || result.startsWith("javascript")) {
    result = null;
  }
  if (result && item?.type !== "eb") {
    result = `/#${result}`;
  }
  return result;
}

function getPredicate(item: string, predicate: (item: string) => boolean) {
  if (item.includes("&&")) {
    return item.split("&&").every((x) => predicate(x));
  }
  if (item.includes("||")) {
    return item.split("||").some((x) => predicate(x));
  }
  return predicate(item);
}

function validateCanDo(item: string, brand: Brand, userRights?: Array<string>) {
  if (!userRights?.length) {
    return false;
  }
  return (
    userRights.includes("all") ||
    getPredicate(item, (x) => {
      // 主店才能修改集团等级
      if (x === "member_level") {
        if (brand.brandType === "brand" && !brand.owned) {
          return false;
        }
      }
      if (x === "brand_order" && brand.dealOrders !== "brand") {
        return false;
      }
      return userRights.includes(x);
    })
  );
}

function validateHotelConfig(item: string, merchat: Merchant) {
  return getPredicate(item, (x) => {
    const [key, value] = x.split("==");
    return merchat.configs[key] === value;
  });
}

function getCustomeValidator(
  user: User,
  merchant: Merchant,
  userRights: string[],
  zhilian: number,
  master: number,
  open: boolean
) {
  return {
    isOwnerHotel() {
      return merchant.brand.owned;
    },
    /**
     * 老集团 || 有自营渠道（公众号 或 小程序）
     */
    hasSelfChannelOrOld() {
      return (
        validateHotelConfig("ENABLE_NEW_SALE==0", merchant) ||
        [
          merchant.brandRelations.publicAccount,
          merchant.brandRelations.applets,
        ].some((relations) =>
          relations.find(
            (item) => item.type === "master" || item.type === "owner"
          )
        )
      );
    },
    showWeekend() {
      const isDistribution = merchant.cooperationPattern === 2;
      return (
        open ||
        (validateHotelConfig("ENABLE_WEEKEND==1", merchant) && isDistribution)
      );
    },
    isBelongToWeekend() {
      return merchant.officeDepartmentId === 5;
    },
    isNotBelongToWeekend() {
      return merchant.officeDepartmentId !== 5;
    },
    isNotZhida() {
      return !(
        merchant.brand.brandId === 173886 && merchant.merchantId !== 183491
      );
    },
    isKtvNotShow() {
      return !(merchant.industryId === 2 || merchant.industryId === 3);
    },
    isOwnerRelation() {
      return merchant.brandRelations.publicAccount.find(
        (item) =>
          item.type === "master" && item.merchantId === merchant.merchantId
      );
    },
    hide() {
      return false;
    },
    isBindBrand() {
      return merchant.brandRelations.publicAccount.some(
        (item) => item.type === "brand"
      );
    },
    bonusQuery() {
      return (
        validateHotelConfig("ENABLE_EMPLOEE_QUERY_ORDER_BONUS==1", merchant) ||
        user.userType === "zkt"
      );
    },
    showPosterNew() {
      const isAdmin = user.userType === "zkt";
      // let ENABLE_NEW_BOARD = this.hotelConfigSingleCheck('ENABLE_NEW_BOARD==1')
      const CAN_ADD_TICKET_PRODUCT = validateHotelConfig(
        "CAN_ADD_TICKET_PRODUCT==1",
        merchant
      );
      const createTicketProduct = validateCanDo(
        "create_ticket_product",
        merchant.brand,
        userRights
      );
      return isAdmin || (CAN_ADD_TICKET_PRODUCT && createTicketProduct);
    },
    isBrand() {
      return merchant.brand.brandType === "brand";
    },
    userTypeNotHotel() {
      return user.userType !== "hotel";
    },
    isNotMitiBrand() {
      return merchant.brandRelations.publicAccount.length <= 1;
    },
    isNotHotel48843() {
      return merchant.merchantId !== 48843;
    },
    isHotel143969() {
      return merchant.merchantId === 143969;
    },
    isPrepaycardDc(param) {
      return String(zhilian) === String(param);
    },
    isPrepaycardSelfMaster(param) {
      return String(master) === String(param);
    },
  };
}

function validateCustomeFunction(
  key: string,
  user: User,
  merchant: Merchant,
  userRights: string[],
  zhilian: number,
  master: number,
  open: boolean
) {
  const result = key.split(",").every((itemFunc) => {
    const [func, ...args] = itemFunc.split(":");
    const validators = getCustomeValidator(
      user,
      merchant,
      userRights,
      zhilian,
      master,
      open
    );
    if (func in validators) {
      return validators[func](...args);
    }
    // eslint-disable-next-line no-console
    console.warn("未定义的显示条件", func);
    return false;
  });
  return result;
}

function validate(
  item,
  merchant: Merchant,
  user: User,
  userRights: string[],
  zhilian: number,
  master: number,
  open: boolean
) {
  if (!item.isShow) return false;
  if (item.canDo && !validateCanDo(item.canDo, merchant.brand, userRights)) {
    return false;
  }
  if (item.hotelConfig && !validateHotelConfig(item.hotelConfig, merchant)) {
    return false;
  }
  if (
    item.conditionSpecial &&
    !validateCustomeFunction(
      item.conditionSpecial,
      user,
      merchant,
      userRights,
      zhilian,
      master,
      open
    )
  ) {
    return false;
  }
  return true;
}

function supportZhilian(hotelId: number) {
  return http
    .request({
      url: "restful/bmw_prepaycard/support_zhilian_by_hotel_id",
      params: { hotel_id: hotelId },
    })
    .then((res) => res.data.response.result)
    .catch(() => undefined);
}

function getPrepaycardSelfMaster(hotelId: number) {
  // return http
  //   .request({
  //     url: "restful/bmw_prepaycard/self_master_hotel",
  //     params: { hotel_id: hotelId },
  //   })
  //   .then((res) => res.data.response.result)
  //   .then((data) => data?.self_master_hotel)
  //   .catch(() => undefined);

  // 采用新接口判断是否单店自营或集团主店======
  return http
    .request({
      url: "prepay-card-api/eb/common/self_master_hotel",
      params: { hotel_id: hotelId },
    })
    .then((data) => {
      return data?.selfMasterHotel;
    })
    .catch(() => undefined);
}
function getDistributionStatus(hotelId: number) {
  return http
    .request({
      url: "restful/bmw_weekend/get_distribution_status",
      params: { hotel_id: hotelId },
    })
    .then((res) => res.data.response.result)
    .then((data) => data?.is_open)
    .catch(() => undefined);
}
export function getMenus(
  merchant: Merchant,
  user: User,
  userRights: string[],
  auth: Auth
): Promise<Array<Menu>> {
  const childrenDict = new Map();
  let showQWKF = true; // 企微推广菜单

  // 中央预订不展示
  if (merchant?.centralBookConfig?.centralBook === 1) {
    showQWKF = false;
  }

  if (merchant) {
    const { brandRelations } = merchant;
    const { publicAccount } = brandRelations;
    // 判断当前是否主店或自营店
    const hasMasterOrOwnerData = publicAccount.find(
      (item) =>
        item.merchantId === auth?.merchantId &&
        (item.type === "master" || item.type === "owner")
    );
    if (!hasMasterOrOwnerData) {
      showQWKF = false;
    }
  }

  // 子店也不展示

  const getMenuPromise = () =>
    getEbMenuGetMenusByUser({ ebVersion: "eb" }).then((res) => res.menus || []);
  return Promise.all([
    supportZhilian(merchant.merchantId),
    getPrepaycardSelfMaster(merchant.merchantId),
    getDistributionStatus(merchant.merchantId),
    getMenuPromise(),
  ]).then(([zhilian, master, open, items]) => {
    const menus: Menu[] = items.reduce((result, item) => {
      if (item.extField?.includes(`"platform":"ebooking"`)) return result;
      if (!validate(item, merchant, user, userRights, zhilian, master, open))
        return result;
      if (item.menuName === "企微推广" && !showQWKF) return result;
      const topLevel = item.treeLevel === 1;
      const mapResult: Menu = {
        id: String(item.sysMenuId),
        name: item.menuName?.trim(),
        target: item.menuTarget,
        link: getLink(item),
        href: "",
        icon: topLevel
          ? React.createElement(icons[item.menuIcon] || Share)
          : null,
        parentId: String(item.parentId),
        parentIds: item.parentCodes.split(",").filter((x) => x),
        messagekey: String(item.menuId),
        children: null,
        menuExtInfo: item.menuExtInfo,
      };
      if (item.extField?.includes("desc")) {
        const ext = JSON.parse(item.extField);
        mapResult.subTitle = ext.desc;
      }
      mapResult.href = mapResult.link;
      if (!childrenDict.has(mapResult.id)) {
        childrenDict.set(mapResult.id, []);
      }
      mapResult.children = childrenDict.get(mapResult.id);
      if (topLevel) {
        result.push(mapResult);
      }
      if (!childrenDict.has(mapResult.parentId)) {
        childrenDict.set(mapResult.parentId, [mapResult]);
      } else {
        childrenDict.get(mapResult.parentId).push(mapResult);
      }
      delete mapResult.parentId;
      return result;
    }, []);
    // console.log("===>menus", menus);
    return menus;
  });
}

export function getNotifyConfig(opts: {
  userId: number;
}): Promise<NotifyConfig> {
  return Promise.resolve({
    wss: `wss://${config.newNotifySocket}/webSocket/${opts.userId}`,
    version: "v2",
  });
}
